import { CurrencyExchangeItem } from '@/models/currencyExchange';
import { createCurrencyExchangePageHeaders } from '@/pages/admin/currencyExchange/CurrencyExchangePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { currencyExchangeService } from '@/services/currencyExchangeService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { IListParams, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createCurrencyExchangeStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<CurrencyExchangeItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- table ---
    const tableHeaders = computed<DataTableHeader[]>(() => createCurrencyExchangePageHeaders());

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<Record<string, never>> = {
                filters: {},
                options: {
                    sortBy: [...defaultSortBy],
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AuditTable'],
            };

            const response = await currencyExchangeService.list(params);
            items.value = (response.data.rows || []).map((row) => new CurrencyExchangeItem(row));
            total.value = response.data.total || 0;
        } catch (error) {
            if (isRequestCanceled(error)) return;
            console.error('Error loading currency exchange:', error);
            items.value = [];
            total.value = 0;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        loading,
        tableHeaders,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: true,
    };
}

export function useCurrencyExchangeStore(storeId: string = 'currencyExchange') {
    const useStore = defineStore(storeId, () => createCurrencyExchangeStoreSetup(storeId));
    return useStore();
}

export type CurrencyExchangeStoreSetup = ReturnType<typeof useCurrencyExchangeStore>;
